import dashify from 'dashify';
import React from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { appEnvironment, formatTwitterLink } from '@benzinga/utils';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { Icon } from '@benzinga/core-ui';
import { FollowAuthorButton } from './FollowAuthorButton';
import styles from './AuthorContent.module.scss';

export interface AuthorContentProps {
  authorName: string;
  authorLink?: string;
  authorType: string;
  authorUid?: number;
  className?: string;
  created: string;
  isDraft?: boolean;
  readTime: string;
  secondaryByline?: string;
  twitter?: string | null;
}

const generateAuthorByLine = (author, authorDetailLink, authorType) => {
  if (author === null) return null;

  let authorName = author || 'Benzinga Staff';

  if (authorDetailLink) {
    authorName =
      authorName !== 'Benzinga Staff' ? (
        <a className="author-name" data-action="Clicked Author Link" href={authorDetailLink}>
          {authorName}
        </a>
      ) : (
        'Benzinga Staff'
      );
  }

  let authorByLine = '';
  if (authorType) {
    authorByLine = `, ${authorType}`;
  }

  return (
    <>
      {authorName}
      {authorByLine}
    </>
  );
};

export const AuthorContent: React.FC<AuthorContentProps> = React.memo(
  ({
    authorLink,
    authorName,
    authorType,
    authorUid,
    className = '',
    created,
    isDraft,
    readTime,
    secondaryByline,
    twitter,
  }) => {
    const authorDetailLink =
      authorLink || (authorName?.trim() ? `${process.env.BASE_URL}/author/${dashify(authorName.toLowerCase())}` : null);
    const articleCreationTime = created
      ? DateTime.fromISO(created, { setZone: true })
          .setZone(appEnvironment().config().zone)
          .toFormat('LLLL d, yyyy h:mm a')
      : null;

    return (
      <div className={classNames(styles.container, 'article-submitted', className)}>
        <div className="article-submitted-content">
          <div>
            <div className="article-author-wrap">
              {authorName !== null && <span>by </span>}
              <span className="article-author">
                {authorName !== null && generateAuthorByLine(authorName, authorDetailLink, authorType)}
              </span>
              {authorUid && <FollowAuthorButton authorName={authorName} authorUid={authorUid} />}
              <a
                aria-label="Find on Twitter"
                className="twitter-link"
                href={formatTwitterLink(twitter || 'benzinga')}
                rel="noreferrer"
                target="_blank"
              >
                <Icon icon={faXTwitter} />
              </a>
            </div>
            <div>
              <span className="secondary-byline">{secondaryByline}</span>
            </div>
          </div>
          <div className="article-date-wrap">
            {isDraft && <span className="draft-badge">Draft</span>}{' '}
            <span className="article-date">{articleCreationTime}</span>{' '}
            {articleCreationTime && <span>| {readTime} </span>}
          </div>
        </div>
      </div>
    );
  },
);
